<template>
  <div class="statistics-center">
    <!-- 工具栏：时间范围 -->
    <div class="toolbar">
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        value-format="YYYY-MM-DD"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        class="toolbar-date"
        :clearable="true"
      />
      <el-button @click="setRange(7)">近 7 天</el-button>
      <el-button @click="setRange(30)">近 30 天</el-button>
      <el-button @click="dateRange = null">全部时间</el-button>
      <el-button type="primary" :loading="loading" @click="fetchData">刷新</el-button>
      <span class="range-text">{{ rangeText }}</span>
    </div>

    <!-- 第一行：总览 + 保内外饼图 + 问题工单 -->
    <el-row :gutter="16">
      <el-col :xs="24" :md="8">
        <el-card shadow="never" class="block-card">
          <template #header><div class="card-header">总体概览（点击数字查看工单）</div></template>
          <div class="total-number clickable" @click="openOrders('全部工单', filteredTickets)">{{ filteredTickets.length }}</div>
          <div class="total-label">时间段内工单总数</div>
          <div class="stat-grid">
            <div class="stat-item clickable" @click="openOrders('保内工单', ticketsOfWarranty('IN_WARRANTY'))">
              <span class="stat-num in">{{ warrantyCounts.IN_WARRANTY }}</span><span class="stat-name">保内单数</span>
            </div>
            <div class="stat-item clickable" @click="openOrders('保外工单', ticketsOfWarranty('OUT_OF_WARRANTY'))">
              <span class="stat-num out">{{ warrantyCounts.OUT_OF_WARRANTY }}</span><span class="stat-name">保外单数</span>
            </div>
            <div class="stat-item clickable" @click="openOrders('已结单工单', filteredTickets.filter(t => t.status === 'CLOSED'))">
              <span class="stat-num">{{ closedCount }}</span><span class="stat-name">已结单</span>
            </div>
            <div class="stat-item clickable" @click="openOrders('进行中工单', filteredTickets.filter(t => t.status !== 'CLOSED'))">
              <span class="stat-num">{{ filteredTickets.length - closedCount }}</span><span class="stat-name">进行中</span>
            </div>
            <div class="stat-item clickable" @click="openOrders('多次处理工单（≥2 次）', repeatedProcessOrders)">
              <span class="stat-num">{{ repeatedProcessOrders.length }}</span><span class="stat-name">多次处理单数</span>
            </div>
            <div class="stat-item">
              <span class="stat-num">{{ totalProcessCount }}</span><span class="stat-name">处理总次数</span>
            </div>
            <div class="stat-item clickable" @click="openOrders('保内工单（成本）', ticketsOfWarranty('IN_WARRANTY'))">
              <span class="stat-num in">¥{{ money(inWarrantyAmount) }}</span><span class="stat-name">保内成本(含人工)</span>
            </div>
            <div class="stat-item clickable" @click="openOrders('保外工单（成本）', ticketsOfWarranty('OUT_OF_WARRANTY'))">
              <span class="stat-num out">¥{{ money(outWarrantyAmount) }}</span><span class="stat-name">保外成本(含人工)</span>
            </div>
            <div class="stat-item">
              <span class="stat-num">{{ totalPartsCount }}</span><span class="stat-name">换件总条目</span>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :md="8">
        <el-card shadow="never" class="block-card">
          <template #header><div class="card-header">保内 / 保外工单占比（点击扇区查看工单）</div></template>
          <BaseChart :option="warrantyPieOption" height="300px" @chart-click="onWarrantyPieClick" />
          <div class="highlight-box">
            <div class="highlight-line clickable" @click="openOrderLogs(mostInWarrantyOrder)">
              <span class="hl-tag in">保内最多</span>
              {{ orderBrief(mostInWarrantyOrder) }}
            </div>
            <div class="highlight-line clickable" @click="openOrderLogs(mostOutWarrantyOrder)">
              <span class="hl-tag out">保外最多</span>
              {{ orderBrief(mostOutWarrantyOrder) }}
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :md="8">
        <el-card shadow="never" class="block-card">
          <template #header><div class="card-header">问题最多的工单（点击查看处理记录）</div></template>
          <div v-if="topProcessOrders.length" class="top-orders">
            <div
              v-for="(item, idx) in topProcessOrders"
              :key="item.id"
              class="top-order-item clickable"
              :class="{ first: idx === 0 }"
              @click="openOrderLogs(item)"
            >
              <span class="rank">{{ idx + 1 }}</span>
              <div class="order-main">
                <div class="order-no">{{ item.workOrderNo || ('#' + item.id) }} · {{ storeLabel(item) }}</div>
                <div class="order-sub">{{ categoryNameOf(item) }}</div>
              </div>
              <span class="process-badge">{{ item.processCount || 0 }} 次处理</span>
            </div>
          </div>
          <el-empty v-else description="暂无数据" :image-size="70" />
        </el-card>
      </el-col>
    </el-row>

    <!-- 多次处理工单（独立展示区） -->
    <el-card shadow="never" class="block-card">
      <template #header>
        <div class="card-header">
          多次处理工单（≥2 次）：共 <b class="hl-num">{{ repeatedProcessOrders.length }}</b> 单，累计处理 <b class="hl-num">{{ repeatedProcessTotal }}</b> 次
          <span class="card-header-sub">点击行查看该单处理记录</span>
        </div>
      </template>
      <el-table
        :data="repeatedProcessOrders"
        v-loading="loading"
        border
        stripe
        size="small"
        max-height="380"
        class="rank-table"
        @row-click="openOrderLogs"
        highlight-current-row
      >
        <el-table-column label="#" width="42">
          <template #default="{ $index }"><span class="rank-cell">{{ $index + 1 }}</span></template>
        </el-table-column>
        <el-table-column prop="workOrderNo" label="工单号" min-width="140" sortable>
          <template #default="{ row }">{{ row.workOrderNo || ('#' + row.id) }}</template>
        </el-table-column>
        <el-table-column prop="storeAuthCode" label="店铺" min-width="160" sortable>
          <template #default="{ row }">{{ storeLabel(row) }}</template>
        </el-table-column>
        <el-table-column prop="categoriesText" label="问题分类" min-width="130" sortable :sort-method="(a, b) => categoryNameOf(a).localeCompare(categoryNameOf(b), 'zh-CN')">
          <template #default="{ row }">{{ categoryNameOf(row) }}</template>
        </el-table-column>
        <el-table-column prop="warrantyResult" label="保内外" width="78" sortable>
          <template #default="{ row }">
            <el-tag size="small" :type="row.warrantyResult === 'IN_WARRANTY' ? 'danger' : row.warrantyResult === 'OUT_OF_WARRANTY' ? 'success' : 'info'">
              {{ warrantyText(row.warrantyResult) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="processCount" label="处理次数" width="88" sortable>
          <template #default="{ row }"><span class="process-badge">{{ row.processCount || 0 }} 次</span></template>
        </el-table-column>
        <el-table-column prop="partsReplacedCount" label="换件" width="62" sortable>
          <template #default="{ row }">{{ row.partsReplacedCount || 0 }}</template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80" sortable>
          <template #default="{ row }">{{ statusText(row.status) }}</template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="108" sortable>
          <template #default="{ row }">{{ formatDate(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="110">
          <template #default>
            <el-button type="primary" text size="small">处理记录</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-if="!loading && !repeatedProcessOrders.length" description="当前时间段没有多次处理的工单" :image-size="70" />
    </el-card>

    <!-- 第二行：分类排行 + 店铺分析 -->
    <el-row :gutter="16">
      <el-col :xs="24" :md="12">
        <el-card shadow="never" class="block-card">
          <template #header><div class="card-header">问题分类排行榜（点击行查看工单明细）</div></template>
          <el-table
            :data="categoryRanking"
            v-loading="loading"
            border
            stripe
            size="small"
            class="rank-table"
            @row-click="openCategoryDialog"
            highlight-current-row
          >
            <el-table-column label="#" width="42">
              <template #default="{ $index }">
                <span :class="['rank-cell', $index === 0 ? 'top1' : '']">{{ $index + 1 }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="name" label="问题分类" min-width="120" sortable />
            <el-table-column prop="count" label="工单数" width="70" sortable />
            <el-table-column prop="inCount" label="保内" width="58" sortable>
              <template #default="{ row }"><span class="cell-in">{{ row.inCount }}</span></template>
            </el-table-column>
            <el-table-column prop="outCount" label="保外" width="58" sortable>
              <template #default="{ row }"><span class="cell-out">{{ row.outCount }}</span></template>
            </el-table-column>
            <el-table-column prop="storeCount" label="店铺数" width="68" sortable />
            <el-table-column prop="processCount" label="处理次数" width="80" sortable />
            <el-table-column label="占比" width="66">
              <template #default="{ row }">{{ ((row.count / (filteredTickets.length || 1)) * 100).toFixed(1) }}%</template>
            </el-table-column>
          </el-table>
          <div v-if="topCategoryConclusion" class="highlight-box">
            <div class="highlight-line">
              <span class="hl-tag">问题最多</span>
              {{ topCategoryConclusion }}
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :md="12">
        <el-card shadow="never" class="block-card">
          <template #header><div class="card-header">问题分类统计 TOP10（点击柱子查看工单明细）</div></template>
          <BaseChart :option="categoryBarOption" height="320px" @chart-click="onCategoryChartClick" />
        </el-card>

        <el-card shadow="never" class="block-card">
          <template #header><div class="card-header">配件更换统计 TOP10（点击柱子查看工单）</div></template>
          <BaseChart :option="partBarOption" height="320px" @chart-click="onPartChartClick" />
        </el-card>

        <el-card shadow="never" class="block-card">
          <template #header><div class="card-header">店铺分析结论（点击查看工单）</div></template>
          <div class="highlight-box">
            <div class="highlight-line clickable" @click="openStoreOrders(storeProcessTop[0])">
              <span class="hl-tag">处理最多</span>
              {{ storeBrief(storeProcessTop[0]) }}
            </div>
            <div class="highlight-line clickable" @click="openStoreOrders(storeReplaceRateTop[0])">
              <span class="hl-tag">换件率最高</span>
              店铺 {{ storeFullLabel(storeReplaceRateTop[0]) }}（{{ replaceRateText(storeReplaceRateTop[0]) }}）
            </div>
            <div class="highlight-line clickable" @click="openStoreOrders(mostRepeatedReplaceStore)">
              <span class="hl-tag">多次更换</span>
              店铺 {{ storeFullLabel(mostRepeatedReplaceStore) }}（{{ repeatedReplaceText(mostRepeatedReplaceStore) }}）
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 通用工单明细弹窗 -->
    <el-dialog v-model="ordersDialogVisible" :title="`${ordersDialogTitle}（${ordersDialogList.length} 单）`" width="960px">
      <el-table :data="ordersDialogList" border stripe size="small" max-height="480" :default-sort="{ prop: 'createdAt', order: 'descending' }">
        <el-table-column prop="workOrderNo" label="工单号" min-width="130" sortable>
          <template #default="{ row }">{{ row.workOrderNo || ('#' + row.id) }}</template>
        </el-table-column>
        <el-table-column prop="storeAuthCode" label="店铺" min-width="150" sortable>
          <template #default="{ row }">{{ storeLabel(row) }}</template>
        </el-table-column>
        <el-table-column prop="productName" label="产品" min-width="100" sortable />
        <el-table-column prop="warrantyResult" label="保内外" width="84" sortable>
          <template #default="{ row }">
            <el-tag size="small" :type="row.warrantyResult === 'IN_WARRANTY' ? 'danger' : row.warrantyResult === 'OUT_OF_WARRANTY' ? 'success' : 'info'">
              {{ warrantyText(row.warrantyResult) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80" sortable>
          <template #default="{ row }">{{ statusText(row.status) }}</template>
        </el-table-column>
        <el-table-column prop="processCount" label="处理次数" width="96" sortable>
          <template #default="{ row }">{{ row.processCount || 0 }}</template>
        </el-table-column>
        <el-table-column prop="partsReplacedCount" label="换件" width="70" sortable>
          <template #default="{ row }">{{ row.partsReplacedCount || 0 }}</template>
        </el-table-column>
        <el-table-column prop="costAmount" label="成本(配件+人工)" width="120" sortable :sort-method="(a, b) => ticketCost(a) - ticketCost(b)">
          <template #default="{ row }">¥{{ money(ticketCost(row)) }}</template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="108" sortable>
          <template #default="{ row }">{{ formatDate(row.createdAt) }}</template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <!-- 处理记录弹窗 -->
    <el-dialog
      v-model="logsDialogVisible"
      :title="`工单 ${logsDialogOrder?.workOrderNo || ('#' + logsDialogOrder?.id)}（${storeLabel(logsDialogOrder || {})}）的处理记录`"
      width="760px"
    >
      <div v-if="logsDialogOrder" class="logs-order-meta">
        {{ categoryNameOf(logsDialogOrder) }} · {{ warrantyText(logsDialogOrder.warrantyResult) }} · 共处理 {{ logsDialogOrder.processCount || 0 }} 次 · 换件 {{ logsDialogParts.length }} 项
      </div>
      <div v-if="logsDialogParts.length" class="parts-block">
        <div class="parts-title">更换的配件（{{ logsDialogParts.length }} 项）</div>
        <el-table :data="logsDialogParts" size="small" border>
          <el-table-column prop="partName" label="配件名称" min-width="130" />
          <el-table-column prop="partModel" label="型号" min-width="90">
            <template #default="{ row }">{{ row.partModel || '—' }}</template>
          </el-table-column>
          <el-table-column label="数量" width="64">
            <template #default="{ row }">×{{ row.quantity || 1 }}</template>
          </el-table-column>
          <el-table-column label="小计" width="90">
            <template #default="{ row }">¥{{ money(row.totalPrice) }}</template>
          </el-table-column>
        </el-table>
      </div>
      <el-timeline v-loading="logsLoading" style="padding-left: 4px; margin-top: 12px">
        <el-timeline-item v-for="(log, idx) in logsDialogList" :key="idx" :timestamp="formatDateTime(log.createdAt || log.followupAt)" placement="top">
          <div class="timeline-title">{{ log.actionType || log.followupType || '处理记录' }}</div>
          <div class="timeline-content">{{ log.actionDetail || log.content || log.remark || '—' }}</div>
          <div class="timeline-meta">记录人：{{ log.operatorName || log.operator || '系统' }}</div>
        </el-timeline-item>
      </el-timeline>
      <el-empty v-if="!logsLoading && !logsDialogList.length" description="暂无处理记录" :image-size="70" />
    </el-dialog>

    <!-- 配件更换排行 -->
    <el-card shadow="never" class="block-card">
      <template #header>
        <div class="card-header">
          配件更换排行（更换最多的配件）
          <span class="card-header-sub">点击行查看更换了该配件的工单</span>
        </div>
      </template>
      <el-table
        :data="partRanking"
        v-loading="loading"
        border
        stripe
        size="small"
        max-height="380"
        class="rank-table"
        @row-click="openPartOrders"
        highlight-current-row
      >
        <el-table-column label="#" width="42">
          <template #default="{ $index }">
            <span :class="['rank-cell', $index === 0 ? 'top1' : '']">{{ $index + 1 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="配件（型号）" min-width="170" sortable />
        <el-table-column prop="orderCount" label="更换单数" width="90" sortable />
        <el-table-column prop="totalQty" label="更换总数" width="90" sortable />
        <el-table-column prop="totalAmount" label="金额合计" width="104" sortable>
          <template #default="{ row }">¥{{ money(row.totalAmount) }}</template>
        </el-table-column>
        <el-table-column label="占比" width="70">
          <template #default="{ row }">{{ ((row.orderCount / (filteredTickets.length || 1)) * 100).toFixed(1) }}%</template>
        </el-table-column>
        <el-table-column label="操作" width="86">
          <template #default>
            <el-button type="primary" text size="small">查看工单</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-if="!loading && !partRanking.length" description="当前时间段没有配件更换记录" :image-size="70" />
    </el-card>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import BaseChart from '@/components/BaseChart.vue'
import { TicketsAPI } from '@/request/tickets'
import { useUserStore } from '@/stores/user'
import { FEATURE_PRODUCTION_ORDERS } from '@/config/featureFlags'

export default {
  name: 'StatisticsCenter',
  components: { BaseChart },
  setup() {
    const userStore = useUserStore()
    const loading = ref(false)
    const tickets = ref([])
    const dateRange = ref(null)

    // 通用工单明细弹窗
    const ordersDialogVisible = ref(false)
    const ordersDialogTitle = ref('')
    const ordersDialogList = ref([])
    const openOrders = (title, list) => {
      if (!list || !list.length) return
      ordersDialogTitle.value = title
      ordersDialogList.value = [...list]
      ordersDialogVisible.value = true
    }

    // 处理记录弹窗（含更换配件明细）
    const logsDialogVisible = ref(false)
    const logsDialogOrder = ref(null)
    const logsDialogList = ref([])
    const logsDialogParts = ref([])
    const logsLoading = ref(false)
    const openOrderLogs = async (order) => {
      if (!order?.id) return
      logsDialogOrder.value = order
      logsDialogList.value = []
      logsDialogParts.value = []
      logsDialogVisible.value = true
      try {
        logsLoading.value = true
        const [logs, parts] = await Promise.all([
          TicketsAPI.getLogs(order.id).catch(() => []),
          TicketsAPI.getPartReplacements(order.id).catch(() => [])
        ])
        logsDialogList.value = Array.isArray(logs) ? logs : []
        logsDialogParts.value = Array.isArray(parts) ? parts : []
      } finally {
        logsLoading.value = false
      }
    }

    const fetchData = async () => {
      try {
        loading.value = true
        const [list, parts] = await Promise.all([
          TicketsAPI.getAll({ sourceType: 'AFTER_SALES' }).catch(() => []),
          TicketsAPI.getPartStats().catch(() => [])
        ])
        tickets.value = Array.isArray(list) ? list : []
        partsRows.value = Array.isArray(parts) ? parts : []
      } catch (err) {
        tickets.value = []
        partsRows.value = []
      } finally {
        loading.value = false
      }
    }

    const setRange = (days) => {
      const end = new Date()
      const start = new Date()
      start.setDate(end.getDate() - (days - 1))
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      dateRange.value = [fmt(start), fmt(end)]
    }

    const filteredTickets = computed(() => {
      if (!dateRange.value || dateRange.value.length !== 2) return tickets.value
      const [s, e] = dateRange.value
      return tickets.value.filter((t) => {
        if (!t.createdAt) return false
        const d = String(t.createdAt).slice(0, 10)
        return (!s || d >= s) && (!e || d <= e)
      })
    })

    // 全量换件明细（与工单日期过滤联动）
    const partsRows = ref([])
    const ticketById = (id) => tickets.value.find((t) => t.id === id)
    const filteredPartsRows = computed(() => {
      const ids = new Set(filteredTickets.value.map((t) => t.id))
      return partsRows.value.filter((r) => ids.has(r.workOrderId))
    })

    // 配件更换排行（更换单数 / 总数量 / 金额）
    const partRanking = computed(() => {
      const map = new Map()
      filteredPartsRows.value.forEach((r) => {
        const key = r.partModel ? `${r.partName}（${r.partModel}）` : String(r.partName || '未知配件')
        if (!map.has(key)) map.set(key, { name: key, orderIds: new Set(), totalQty: 0, totalAmount: 0 })
        const item = map.get(key)
        item.orderIds.add(r.workOrderId)
        item.totalQty += Number(r.quantity || 0)
        item.totalAmount += Number(r.totalPrice || 0)
      })
      return [...map.values()]
        .map((x) => ({ ...x, orderCount: x.orderIds.size }))
        .sort((a, b) => b.orderCount - a.orderCount || b.totalQty - a.totalQty)
    })

    const openPartOrders = (row) => {
      if (!row) return
      const orders = [...row.orderIds].map(ticketById).filter(Boolean)
      openOrders(`更换配件「${row.name}」的工单`, orders)
    }

    const rangeText = computed(() => {
      if (!dateRange.value || dateRange.value.length !== 2) return `全部时间 · 共 ${tickets.value.length} 单`
      return `${dateRange.value[0]} 至 ${dateRange.value[1]} · 原始 ${tickets.value.length} 单`
    })

    const money = (v) => Number(v || 0).toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })

    // 店铺标注：授权码 + 所属合作连锁店
    const storeLabel = (t) => {
      const code = t.storeAuthCode || '未登记'
      return t.cooperativeChainName ? `${code}（${t.cooperativeChainName}）` : String(code)
    }

    const ticketsOfWarranty = (w) => filteredTickets.value.filter((t) => t.warrantyResult === w)

    const warrantyCounts = computed(() => {
      const c = { IN_WARRANTY: 0, OUT_OF_WARRANTY: 0, UNKNOWN: 0 }
      filteredTickets.value.forEach((t) => {
        const k = t.warrantyResult || 'UNKNOWN'
        c[k] = (c[k] || 0) + 1
      })
      return c
    })

    const closedCount = computed(() => filteredTickets.value.filter((t) => t.status === 'CLOSED').length)
    const totalProcessCount = computed(() => filteredTickets.value.reduce((s, t) => s + (t.processCount || 0), 0))
    const totalPartsCount = computed(() => filteredTickets.value.reduce((s, t) => s + (t.partsReplacedCount || 0), 0))
    // 金额口径 = 成本（配件 + 人工），保内保外统一按实际成本核算
    const ticketCost = (t) => Number(t.partTotalAmount || 0) + Number(t.laborAmount || 0)
    const inWarrantyAmount = computed(() => ticketsOfWarranty('IN_WARRANTY').reduce((s, t) => s + ticketCost(t), 0))
    const outWarrantyAmount = computed(() => ticketsOfWarranty('OUT_OF_WARRANTY').reduce((s, t) => s + ticketCost(t), 0))

    // 多次处理（重复处理）统计：独立展示区数据，按处理次数降序
    const repeatedProcessOrders = computed(() =>
      filteredTickets.value.filter((t) => (t.processCount || 0) >= 2).sort((a, b) => (b.processCount || 0) - (a.processCount || 0))
    )
    const repeatedProcessTotal = computed(() => repeatedProcessOrders.value.reduce((s, t) => s + (t.processCount || 0), 0))

    const orderBrief = (order) => {
      if (!order) return '暂无数据'
      return `${order.workOrderNo || ('#' + order.id)} · ${storeLabel(order)} — 处理 ${order.processCount || 0} 次`
    }

    const byProcess = (list) => [...list].sort((a, b) => (b.processCount || 0) - (a.processCount || 0))[0] || null
    const mostInWarrantyOrder = computed(() => byProcess(ticketsOfWarranty('IN_WARRANTY')))
    const mostOutWarrantyOrder = computed(() => byProcess(ticketsOfWarranty('OUT_OF_WARRANTY')))
    const topProcessOrders = computed(() =>
      [...filteredTickets.value].sort((a, b) => (b.processCount || 0) - (a.processCount || 0)).slice(0, 5)
    )

    const categoryNameOf = (t) => {
      const names = (t.categories || []).map((c) => c.name).filter(Boolean)
      return names.length ? names.join(' / ') : '未分类'
    }

    // 分类排行（详细：保内/保外/店铺数/处理次数）
    const categoryRanking = computed(() => {
      const map = new Map()
      filteredTickets.value.forEach((t) => {
        const cats = (t.categories || []).map((c) => c.name).filter(Boolean)
        const keys = cats.length ? cats : ['未分类']
        keys.forEach((name) => {
          if (!map.has(name)) map.set(name, { name, count: 0, inCount: 0, outCount: 0, stores: new Set(), processCount: 0, orders: [] })
          const item = map.get(name)
          item.count += 1
          item.processCount += t.processCount || 0
          if (t.storeAuthCode) item.stores.add(t.storeAuthCode)
          if (t.warrantyResult === 'IN_WARRANTY') item.inCount += 1
          else if (t.warrantyResult === 'OUT_OF_WARRANTY') item.outCount += 1
          item.orders.push(t)
        })
      })
      return [...map.values()]
        .map((x) => ({ ...x, storeCount: x.stores.size }))
        .sort((a, b) => b.count - a.count)
    })

    const topCategoryConclusion = computed(() => {
      const top = categoryRanking.value[0]
      if (!top) return ''
      const side = top.inCount > top.outCount ? '保内' : top.outCount > top.inCount ? '保外' : '保内保外各半'
      return `「${top.name}」${top.count} 单，以${side}为主（保内 ${top.inCount} / 保外 ${top.outCount}），涉及 ${top.storeCount} 家店铺`
    })

    const openCategoryDialog = (row) => openOrders(`问题分类「${row.name}」的工单`, row.orders)

    // 店铺统计（标注所属连锁店）
    const storeStats = computed(() => {
      const map = new Map()
      filteredTickets.value.forEach((t) => {
        const key = t.storeAuthCode || '未登记'
        if (!map.has(key)) {
          map.set(key, { store: key, chain: t.cooperativeChainName || '', orderCount: 0, processCount: 0, replacedOrderCount: 0, partsCount: 0, repeatedOrders: 0, orders: [] })
        }
        const s = map.get(key)
        if (!s.chain && t.cooperativeChainName) s.chain = t.cooperativeChainName
        s.orderCount += 1
        s.processCount += t.processCount || 0
        s.partsCount += t.partsReplacedCount || 0
        if ((t.partsReplacedCount || 0) > 0) s.replacedOrderCount += 1
        if ((t.partsReplacedCount || 0) >= 2) s.repeatedOrders += 1
        s.orders.push(t)
      })
      return [...map.values()]
    })

    const storeFullLabel = (s) => (s ? (s.chain ? `${s.store}（${s.chain}）` : s.store) : '—')
    const storeBrief = (s) => (s ? `${storeFullLabel(s)} — 处理 ${s.processCount} 次 / ${s.orderCount} 单` : '暂无数据')
    const replaceRateText = (s) => (s ? `${((s.replacedOrderCount / s.orderCount) * 100).toFixed(0)}%（${s.orderCount} 单中 ${s.replacedOrderCount} 单换件）` : '')
    const repeatedReplaceText = (s) => (s && s.repeatedOrders > 0 ? `${s.repeatedOrders} 单多次更换，累计换件 ${s.partsCount} 条` : '暂无')

    const storeProcessTop = computed(() => [...storeStats.value].sort((a, b) => b.processCount - a.processCount).slice(0, 10))
    const storeReplaceRateTop = computed(() =>
      [...storeStats.value]
        .filter((s) => s.orderCount >= 2)
        .sort((a, b) => b.replacedOrderCount / b.orderCount - a.replacedOrderCount / a.orderCount || b.orderCount - a.orderCount)
        .slice(0, 10)
    )
    const mostRepeatedReplaceStore = computed(() =>
      [...storeStats.value].sort((a, b) => b.repeatedOrders - a.repeatedOrders || b.partsCount - a.partsCount)[0] || null
    )

    const openStoreOrders = (s) => {
      if (!s) return
      openOrders(`店铺 ${storeFullLabel(s)} 的工单`, s.orders)
    }

    const onStoreChartClick = (params, rows) => {
      const s = (rows || []).find((r) => String(r.store) === String(params.name))
      openStoreOrders(s)
    }

    const onWarrantyPieClick = (params) => {
      if (params.name === '保内') openOrders('保内工单', ticketsOfWarranty('IN_WARRANTY'))
      else if (params.name === '保外') openOrders('保外工单', ticketsOfWarranty('OUT_OF_WARRANTY'))
      else openOrders('未判定保修的工单', ticketsOfWarranty('UNKNOWN'))
    }

    // 图表（保内红 / 保外绿 —— 按要求对调）
    const warrantyPieOption = computed(() => ({
      tooltip: { trigger: 'item', formatter: '{b}：{c} 单（{d}%）' },
      legend: { bottom: 0 },
      series: [
        {
          type: 'pie',
          radius: ['38%', '66%'],
          center: ['50%', '46%'],
          label: { formatter: '{b}\n{c} 单' },
          data: [
            { name: '保内', value: warrantyCounts.value.IN_WARRANTY, itemStyle: { color: '#f56c6c' } },
            { name: '保外', value: warrantyCounts.value.OUT_OF_WARRANTY, itemStyle: { color: '#67c23a' } },
            { name: '未判定', value: warrantyCounts.value.UNKNOWN, itemStyle: { color: '#909399' } }
          ]
        }
      ]
    }))

    const makeHBarOption = (rows, nameKey, valueKey, color, unit, valueFmt) => {
      const reversed = [...rows].reverse()
      return {
        tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}：${valueFmt ? valueFmt(rows.find(r => r[nameKey] === p[0].name)) : p[0].value + ' ' + unit}` },
        grid: { left: 8, right: 46, top: 10, bottom: 10, containLabel: true },
        xAxis: { type: 'value', minInterval: 1 },
        yAxis: { type: 'category', data: reversed.map((r) => r[nameKey]), axisLabel: { width: 96, overflow: 'truncate' } },
        series: [
          {
            type: 'bar',
            data: reversed.map((r) => r[valueKey]),
            barMaxWidth: 18,
            itemStyle: { color },
            label: { show: true, position: 'right', formatter: '{c}' }
          }
        ]
      }
    }

    /** 问题分类统计图（TOP10，按工单数） */
    const categoryBarOption = computed(() => makeHBarOption(categoryRanking.value.slice(0, 10), 'name', 'count', '#409eff', '单'))
    /** 配件更换统计图（TOP10，按更换单数） */
    const partBarOption = computed(() => makeHBarOption(partRanking.value.slice(0, 10), 'name', 'orderCount', '#e6a23c', '单',
      (r) => `${r.orderCount} 单 / ${r.totalQty} 件 / ¥${money(r.totalAmount)}`))

    const onCategoryChartClick = (params) => {
      const row = categoryRanking.value.find((r) => r.name === params.name)
      if (row) openCategoryDialog(row)
    }

    const onPartChartClick = (params) => {
      const row = partRanking.value.find((r) => r.name === params.name)
      openPartOrders(row)
    }

    const warrantyText = (v) => ({ IN_WARRANTY: '保内', OUT_OF_WARRANTY: '保外' }[v] || '未判定')
    const statusText = (v) => ({ PENDING: '待处理', PROCESSING: '处理中', CLOSED: '已结单', CANCELLED: '已取消' }[v] || v || '—')
    const formatDateTime = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '—')
    const formatDate = (v) => (v ? String(v).slice(0, 10) : '—')

    onMounted(fetchData)

    return {
      userStore,
      FEATURE_PRODUCTION_ORDERS,
      loading,
      dateRange,
      rangeText,
      setRange,
      fetchData,
      money,
      storeLabel,
      filteredTickets,
      ticketsOfWarranty,
      warrantyCounts,
      closedCount,
      totalProcessCount,
      totalPartsCount,
      inWarrantyAmount,
      outWarrantyAmount,
      ticketCost,
      repeatedProcessOrders,
      repeatedProcessTotal,
      mostInWarrantyOrder,
      mostOutWarrantyOrder,
      topProcessOrders,
      categoryNameOf,
      orderBrief,
      categoryRanking,
      topCategoryConclusion,
      openCategoryDialog,
      storeStats,
      storeProcessTop,
      storeReplaceRateTop,
      mostRepeatedReplaceStore,
      storeFullLabel,
      storeBrief,
      replaceRateText,
      repeatedReplaceText,
      openStoreOrders,
      onStoreChartClick,
      onWarrantyPieClick,
      warrantyPieOption,
      categoryBarOption,
      partBarOption,
      onCategoryChartClick,
      onPartChartClick,
      ordersDialogVisible,
      ordersDialogTitle,
      ordersDialogList,
      openOrders,
      openOrderLogs,
      logsDialogVisible,
      logsDialogOrder,
      logsDialogList,
      logsDialogParts,
      logsLoading,
      partRanking,
      openPartOrders,
      warrantyText,
      statusText,
      formatDateTime,
      formatDate
    }
  }
}
</script>

<style scoped>
.statistics-center { padding: 4px; }
.toolbar { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 14px; }
.toolbar-date { width: 280px; }
.range-text { color: #909399; font-size: 13px; }
.block-card { margin-bottom: 16px; }
.card-header { font-weight: 600; }
.card-header-sub { font-size: 12px; color: #909399; font-weight: 400; margin-left: 8px; }
.hl-num { color: #e6a23c; font-size: 16px; }
.clickable { cursor: pointer; }
.clickable:hover { opacity: 0.82; }
.total-number { font-size: 52px; font-weight: 800; color: #409eff; line-height: 1.1; text-align: center; }
.total-label { text-align: center; color: #909399; margin-bottom: 14px; }
.stat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.stat-item { display: flex; flex-direction: column; align-items: center; background: #f5f7fa; border-radius: 8px; padding: 10px 4px; }
.stat-num { font-size: 19px; font-weight: 700; color: #303133; }
.stat-num.in { color: #f56c6c; }
.stat-num.out { color: #67c23a; }
.stat-name { font-size: 12px; color: #909399; margin-top: 2px; }
.highlight-box { margin-top: 8px; display: grid; gap: 6px; }
.highlight-line { font-size: 13px; color: #606266; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.hl-tag { background: #409eff; color: #fff; border-radius: 4px; padding: 1px 8px; font-size: 12px; white-space: nowrap; }
.hl-tag.in { background: #f56c6c; }
.hl-tag.out { background: #67c23a; }
.repeat-summary { background: #fdf6ec; border: 1px solid #faecd8; color: #b88230; border-radius: 8px; padding: 8px 12px; font-size: 13px; margin-bottom: 10px; }
.top-orders { display: grid; gap: 8px; }
.top-order-item { display: flex; align-items: center; gap: 10px; border: 1px solid #ebeef5; border-radius: 8px; padding: 8px 12px; }
.top-order-item.first { border-color: #409eff; background: #ecf5ff; }
.rank { width: 22px; height: 22px; border-radius: 50%; background: #ebeef5; color: #606266; font-size: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.top-order-item.first .rank { background: #409eff; color: #fff; }
.order-main { flex: 1; min-width: 0; }
.order-no { font-size: 13px; font-weight: 600; color: #303133; }
.order-sub { font-size: 12px; color: #909399; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.process-badge { font-size: 12px; color: #409eff; font-weight: 700; white-space: nowrap; }
.rank-table { cursor: pointer; }
.rank-cell { font-weight: 700; color: #909399; }
.rank-cell.top1 { color: #e6a23c; }
.cell-in { color: #f56c6c; font-weight: 700; }
.cell-out { color: #67c23a; font-weight: 700; }
.logs-order-meta { color: #606266; font-size: 13px; background: #f5f7fa; border-radius: 8px; padding: 8px 12px; }
.parts-block { margin-top: 12px; }
.parts-title { font-size: 13px; font-weight: 600; color: #b88230; margin-bottom: 6px; }
.timeline-title { font-weight: 600; font-size: 13px; color: #303133; }
.timeline-content { font-size: 13px; color: #606266; margin: 2px 0; }
.timeline-meta { font-size: 12px; color: #909399; }
</style>
